import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from './api';
import { BUDDY_NAV } from './buddy/nav';
import { PRINTQUEUE_NAV } from './printqueue/nav';
import { AUTOMATION_NAV } from './automation/nav';
import { DESIGN_NAV } from './design/nav';
import { BILLING_NAV } from './billing/nav';
import { ONBOARDING_NAV } from './onboarding/nav';
import { INDEX_ADVISOR_NAV } from './indexadvisor/nav';
import { PRODUCTION_NAV } from './prodstats/nav';
import { canOpenPath, visibleTree, type NavAllow, type NavNode } from '@nyce/admin-kit/console';

export type { NavNode } from '@nyce/admin-kit/console';

/**
 * The menu in screen order: one node per sidebar group, its pages as children (a sub-group is a child with children).
 * A group with a `path` opens its group dashboard from its name (the chevron next to it folds it); a group without
 * children (Overview) is just that link.
 */
export const NAV: NavNode[] = [
  { id: 'overview', label: 'Overview', path: '/dashboard' },
  {
    id: 'operations',
    label: 'Operations',
    path: '/operations',
    children: [
      {
        path: '/logs',
        label: 'Logs',
        requires: SCOPES.gcplogsRead,
        description: 'Query and tail GCP logs per environment.',
      },
      {
        path: '/pods',
        label: 'Pods',
        requires: SCOPES.podsRead,
        description: 'GKE workloads: status, restarts, scaling.',
      },
      {
        path: '/databases',
        label: 'Databases',
        requires: SCOPES.dbRead,
        description: 'Database overview, backups and copies.',
      },
      ...INDEX_ADVISOR_NAV,
    ],
  },
  {
    id: 'statistics',
    label: 'Statistics',
    path: '/statistics',
    children: [
      ...PRODUCTION_NAV,
      {
        path: '/statistics/orgs',
        label: 'Organizations',
        requires: SCOPES.statsRead,
        description: 'Collector data per organization: installations, performance, volumes, errors.',
      },
      {
        path: '/statistics/incidents',
        label: 'Incidents',
        requires: SCOPES.statsRead,
        description: 'Performance incidents: acknowledge and close.',
      },
      {
        path: '/statistics/errors',
        label: 'Error groups',
        requires: SCOPES.statsRead,
        description: 'WorkServer error logs grouped by message fingerprint.',
      },
      {
        path: '/statistics/reports',
        label: 'Reports',
        requires: SCOPES.statsRead,
        description: 'Scheduled PDF report subscriptions, preview and test send.',
      },
      {
        path: '/statistics/screen',
        label: 'TV screen',
        requires: SCOPES.statsRead,
        description: 'Full-screen fleet status for the office screen.',
      },
      {
        path: '/statistics/import',
        label: 'Import from PMS',
        requires: SCOPES.statsRead,
        alsoRequires: [SCOPES.authWrite],
        description: 'Link collector organizations to AUTH customers (pms alias).',
      },
    ],
  },
  {
    id: 'buddy',
    label: 'Buddy',
    path: '/buddy',
    children: BUDDY_NAV,
  },
  {
    id: 'printqueue',
    label: 'Print queue',
    path: '/printqueue',
    children: PRINTQUEUE_NAV,
  },
  {
    id: 'automation',
    label: 'Automation',
    path: '/automation',
    children: AUTOMATION_NAV,
  },
  {
    id: 'design',
    label: 'NYCE Design',
    children: DESIGN_NAV,
  },
  {
    id: 'billing',
    label: 'Billing',
    path: '/billing',
    children: BILLING_NAV,
  },
  {
    id: 'identity',
    label: 'Identity',
    path: '/identity',
    children: [
      {
        path: '/identity/tenants',
        label: 'Tenants',
        requires: SCOPES.authRead,
        description: 'AUTH tenants per environment: claims, roles, keys, users.',
      },
      {
        path: '/identity/customers',
        label: 'Customers',
        requires: SCOPES.authRead,
        description: 'The organizations AUTH tenants belong to.',
      },
      {
        path: '/identity/scim',
        label: 'SCIM endpoints',
        requires: SCOPES.scimRead,
        description: 'Where the SCIM gateway sends each tenant’s provisioning.',
      },
      {
        path: '/identity/login-apps',
        label: 'Login apps',
        requires: SCOPES.authRead,
        description: 'Browser apps allowed to use single sign-on (redirect URIs).',
      },
      {
        path: '/identity/access',
        label: 'AUTH access',
        requires: SCOPES.authRead,
        description: 'Your token on each AUTH environment.',
      },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    path: '/onboarding',
    children: ONBOARDING_NAV,
  },
  {
    id: 'tools',
    label: 'Tools',
    children: [
      {
        path: '/tools',
        label: 'Linked tools',
        description: 'Other nyce.logic admin tools, with pass-through sign-in where supported.',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    path: '/admin',
    children: [
      {
        path: '/admin/clusters',
        label: 'Clusters',
        requires: SCOPES.admin,
        description: 'GKE clusters the hub talks to.',
      },
      {
        path: '/admin/environments',
        label: 'Environments',
        requires: SCOPES.admin,
        description: 'Installations: cluster, namespaces, database.',
      },
      {
        path: '/admin/db-servers',
        label: 'DB servers',
        requires: SCOPES.admin,
        description: 'Postgres servers and their credentials.',
      },
      {
        path: '/admin/links',
        label: 'Links',
        requires: SCOPES.admin,
        description: 'The tools shown under Linked tools.',
      },
      {
        path: '/admin/audit',
        label: 'Audit',
        requires: SCOPES.admin,
        description: 'Every write made through the hub.',
      },
    ],
  },
];

const allowed =
  (scopes: readonly string[] | null | undefined): NavAllow =>
  (n) =>
    hasScope(scopes, n.requires) && (n.alsoRequires ?? []).every((s) => hasScope(scopes, s));

/** The part of the menu a caller with `scopes` may see (a group with no visible page disappears). */
export const visibleNav = (scopes: readonly string[] | null | undefined): NavNode[] =>
  visibleTree(NAV, allowed(scopes));

/** May the caller open `path` (a page or a group dashboard). */
export const canOpen = (scopes: readonly string[] | null | undefined, path: string): boolean =>
  canOpenPath(NAV, allowed(scopes), path);

/** Where `/` goes: the Dashboard is open to everyone signed in. */
export const HOME = '/dashboard';
